import styled from "styled-components"

import { spacing, Text } from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"

import { ParadiseOverviewExpandable } from "src/components/Paradise/ParadiseOverviewExpandable"
import { ParadiseSettingStatic } from "src/components/Paradise/ParadiseSettings/ParadiseSettingStatic"
import { ParadiseSettingText } from "src/components/Paradise/ParadiseSettings/ParadiseSettingText"
import { ParadiseUserRoles } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserOverview/ParadiseUserRoles"
import { CopyWrapper } from "src/components/Paradise/sharedStyles"
import { TSettingContainerOnSaveReturnType } from "src/components/Settings/Setting/SettingContainer"
import { TFetchCurrentSubscriptionResponse } from "src/data/billing/types/billingTypes"
import { usePutParadiseUser } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { TParadiseUser } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { isSuperAdmin } from "src/data/user/logic/accessLogic"
import { CopyText } from "src/ui/CopyText/CopyText"
import { formatUtcDate } from "src/utils/l10n"

export function ParadiseUserOverview({
  user,
  subscription,
}: {
  user: TParadiseUser
  subscription?: TFetchCurrentSubscriptionResponse | null
}) {
  const putParadiseUser = usePutParadiseUser()

  const loggedInUser = useGetUser()

  async function savePhoneNumber(
    newPhoneNumber: string
  ): TSettingContainerOnSaveReturnType {
    try {
      await putParadiseUser.mutateAsync({
        path: {
          user_id: user.id,
        },
        body: {
          phone_number: newPhoneNumber,
        },
      })

      return {
        isSuccess: true,
      }
    } catch (error) {
      return {
        isSuccess: false,
      }
    }
  }

  return (
    <ParadiseOverviewExpandable rawJson={user}>
      <ParadiseSettingStatic
        title="User id"
        value={
          <CopyWrapper>
            <Text variant="bodyS">{user.id}</Text>
            <CopyText value={user.id} copiedText="Copied user id" />
          </CopyWrapper>
        }
      />
      <ParadiseSettingStatic title="Name" value={user.full_name} />
      <ParadiseSettingStatic
        title="Email"
        value={
          <EmailWrapper>
            <CopyText
              value={user.email}
              displayText={user.email}
              copiedText="Copied email"
            />
            <div>
              <Badge
                size="x-small"
                color={user.email_verification?.verified ? "good" : "neutral"}
              >
                {user.email_verification?.verified ? "Verified" : "Unverified"}
              </Badge>
            </div>
          </EmailWrapper>
        }
      />
      <ParadiseSettingText
        title="Phone number"
        value={user.phone_number ?? ""}
        onSave={savePhoneNumber}
        disabled={!isSuperAdmin(loggedInUser.roles)}
      />
      <ParadiseSettingStatic
        title="Created at"
        value={formatUtcDate({ date: user.created_at })}
      />
      <ParadiseSettingStatic
        title="Updated at"
        value={formatUtcDate({ date: user.updated_at })}
      />
      <ParadiseSettingStatic
        title="Last seen at"
        value={formatUtcDate({ date: user.last_seen_at ?? "" })}
      />
      <ParadiseSettingStatic
        title="Subscription"
        value={
          user.subscription_plan_id ? (
            <SubscriptionWrapper>
              <div>{user.subscription_plan_id}</div>
              <SubscriptionBadgeWrapper>
                <Badge color="good" size="small">
                  {user.subscription_status}
                </Badge>

                {subscription && (
                  <Badge color="info" size="small">
                    {subscription.billing_model}
                  </Badge>
                )}
              </SubscriptionBadgeWrapper>
            </SubscriptionWrapper>
          ) : (
            "-"
          )
        }
      />
      <ParadiseUserRoles currentRoles={user.roles} userId={user.id} />
    </ParadiseOverviewExpandable>
  )
}

const SubscriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const EmailWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.M};
  flex-wrap: wrap;
`

const SubscriptionBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.S};
`
